import { cached } from "@glimmer/tracking";
import { getOwner } from "@ember/owner";
import { service } from "@ember/service";
import { bind } from "discourse/lib/decorators";
import EmbedMode from "discourse/lib/embed-mode";
import getURL, { withoutPrefix } from "discourse/lib/get-url";
import { withPluginApi } from "discourse/lib/plugin-api";
import BaseCustomSidebarSection from "discourse/lib/sidebar/base-custom-sidebar-section";
import BaseCustomSidebarSectionLink from "discourse/lib/sidebar/base-custom-sidebar-section-link";
import EverythingSectionLink from "discourse/lib/sidebar/common/community-section/everything-section-link";
import { i18n } from "discourse-i18n";
import CommunityGameOrderHandle from "../components/community-game-order-handle";
import CommunityNavigationHeaderMenu from "../components/community-navigation-header-menu";
import CommunitySecondaryNavigation from "../components/community-secondary-navigation";
import selection from "../lib/community-navigation-selection";

const PANEL_KEY = "community-navigation";

function coreOwnsSidebarPanel(pathname) {
  return pathname === "/admin" || pathname.startsWith("/admin/");
}

class PrimaryLink extends BaseCustomSidebarSectionLink {
  constructor({
    key,
    text,
    href,
    route,
    icon,
    imageUrl,
    suffixComponent,
    suffixArgs,
  }) {
    super();
    this.key = key;
    this.label = text;
    this.url = href;
    this.routeName = route;
    this.icon = icon;
    this.imageUrl = imageUrl;
    this.linkSuffixComponent = suffixComponent;
    this.linkSuffixArgs = suffixArgs;
  }

  get name() {
    return this.key.replaceAll(".", "-");
  }

  get text() {
    return this.label;
  }

  get title() {
    return this.label;
  }

  get href() {
    return this.url;
  }

  get route() {
    return this.routeName;
  }

  get prefixType() {
    return this.imageUrl ? "image" : "icon";
  }

  get prefixValue() {
    return this.imageUrl || this.icon;
  }

  get suffixComponent() {
    return this.linkSuffixComponent;
  }

  get suffixArgs() {
    return this.linkSuffixArgs;
  }

  get currentWhen() {
    return selection.primaryKey === this.key;
  }
}

class PublicPrimaryLink extends EverythingSectionLink {
  get text() {
    return i18n("community_navigation.public");
  }

  get title() {
    return i18n("community_navigation.public");
  }

  get currentWhen() {
    return selection.primaryKey === "public";
  }
}

class PrimarySection extends BaseCustomSidebarSection {
  @service communityNavigation;
  @service topicTrackingState;

  @bind
  willDestroy() {
    if (this.topicTrackingCallbackId) {
      this.topicTrackingState.offStateChange(this.topicTrackingCallbackId);
    }

    this.links.forEach((link) => link.teardown?.());
  }

  get name() {
    return "community-navigation-primary";
  }

  get text() {
    return i18n("community_navigation.navigation");
  }

  @cached
  get links() {
    const links = [
      new PublicPrimaryLink(getOwner(this), {
        overriddenName: i18n("community_navigation.public"),
      }),
      new PrimaryLink({
        key: "mods",
        text: i18n("community_navigation.mod_market"),
        href: getURL("/mods"),
        icon: "store",
      }),
      new PrimaryLink({
        key: "mod",
        text: i18n("community_navigation.mod_community"),
        href: getURL("/mod-community"),
        icon: "puzzle-piece",
      }),
    ];
    const creator = this.communityNavigation.tree?.primary?.find(
      (item) => item.key === "creator"
    );
    if (creator) {
      links.push(
        new PrimaryLink({
          key: "creator",
          text: creator.label,
          route: "creator-community",
          icon: "pen-nib",
        })
      );
    }

    this.topicTrackingCallbackId = this.topicTrackingState?.onStateChange(() =>
      links[0].onTopicTrackingStateChange()
    );

    return links;
  }
}

class GamesSection extends BaseCustomSidebarSection {
  @service communityGameOrder;

  get name() {
    return "community-navigation-games";
  }

  get text() {
    return i18n("community_navigation.games");
  }

  @cached
  get links() {
    return this.communityGameOrder.games.map((game) => {
      const key = game.game_key;
      const gameName =
        game.name || game.label || i18n(`community_navigation.game_names.${key}`);
      return new PrimaryLink({
        key: `game.${key}`,
        text: gameName,
        href: getURL(game.href || `/games/${key}`),
        icon: "gamepad",
        imageUrl: game.icon_url,
        suffixComponent: CommunityGameOrderHandle,
        suffixArgs: { gameKey: key, gameName },
      });
    });
  }
}

export default {
  name: "community-navigation",

  initialize(container) {
    const siteSettings = container.lookup("service:site-settings");
    if (!siteSettings.community_navigation_enabled || EmbedMode.enabled) {
      return;
    }

    const navigation = container.lookup("service:community-navigation");
    const sidebarState = container.lookup("service:sidebar-state");

    withPluginApi((api) => {
      api.addSidebarPanel(
        (BaseCustomSidebarPanel) =>
          class CommunityNavigationPanel extends BaseCustomSidebarPanel {
            key = PANEL_KEY;
            hidden = true;
            displayHeader = false;
            expandActiveSection = true;
            sections = [PrimarySection, GamesSection];
          }
      );

      api.renderInOutlet("before-main-outlet", CommunitySecondaryNavigation);
      api.headerIcons.add(
        "community-navigation-menu",
        CommunityNavigationHeaderMenu,
        { after: "hamburger" }
      );

      const applySidebarPanel = (pathname) => {
        if (coreOwnsSidebarPanel(pathname)) {
          return;
        }

        if (navigation.active) {
          api.setSidebarPanel(PANEL_KEY);
          sidebarState.setSeparatedMode();
        } else if (pathname.startsWith("/chat")) {
          api.setSidebarPanel("chat");
        } else {
          api.setSidebarPanel("main");
        }
      };

      const update = async (path) => {
        const pathname = withoutPrefix(
          new URL(path, window.location.origin).pathname
        );
        const updatePromise = navigation.update(path, container);

        applySidebarPanel(pathname);
        await updatePromise;
        applySidebarPanel(pathname);
      };

      api.onPageChange(update);
      update(window.location.pathname + window.location.search);
    });
  },
};
